import React from 'react';

import { AllocationType, Strategy, StrategyType } from '@ndvr/portfolio-api';

import { Button } from '@/components/ui/button';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Tabs, TabsList, TabsTrigger } from '@/components/ui/tabs';

import { BLACKROCK_TAB, StrategyTabKey } from '../hooks/useStrategyFilter';

interface AddStrategySectionProps {
    availableStrategies: Strategy[];
    selectedStrategyGUID: string;
    onStrategySelect: (guid: string) => void;
    onAdd: () => void;
    onCancel: () => void;
    strategyTypeFilter: StrategyTabKey;
    onTypeFilterChange: (type: StrategyTabKey) => void;
    allocationType: AllocationType;
    showBlackrock?: boolean;
}

export function AddStrategySection({
    availableStrategies,
    selectedStrategyGUID,
    onStrategySelect,
    onAdd,
    onCancel,
    strategyTypeFilter,
    onTypeFilterChange,
    allocationType,
    showBlackrock = false,
}: AddStrategySectionProps) {
    return (
        <div className="space-y-3">
            <Tabs value={strategyTypeFilter} onValueChange={(val) => onTypeFilterChange(val as StrategyTabKey)}>
                <TabsList className="border-none">
                    <TabsTrigger value={StrategyType.CORE}>Core Strategies</TabsTrigger>
                    {showBlackrock && <TabsTrigger value={BLACKROCK_TAB}>BlackRock Strategies</TabsTrigger>}
                    <TabsTrigger value={StrategyType.SHARED}>Shared Strategies</TabsTrigger>
                    {allocationType !== AllocationType.SHARED && (
                        <TabsTrigger value={StrategyType.PERSONAL}>My Strategies</TabsTrigger>
                    )}
                </TabsList>
            </Tabs>

            <Select value={selectedStrategyGUID} onValueChange={onStrategySelect}>
                <SelectTrigger>
                    <SelectValue placeholder="Select a strategy..." />
                </SelectTrigger>
                <SelectContent>
                    {availableStrategies.length === 0 ? (
                        <div className="px-2 py-6 text-center text-sm text-gray-500">No strategies available</div>
                    ) : (
                        availableStrategies
                            .sort((a, b) => a.strategyName.localeCompare(b.strategyName))
                            .map((strategy) => (
                                <SelectItem key={strategy.strategyGUID} value={strategy.strategyGUID}>
                                    {strategy.strategyName}
                                </SelectItem>
                            ))
                    )}
                </SelectContent>
            </Select>

            <div className="flex gap-2">
                <Button
                    type="button"
                    variant="default"
                    size="sm"
                    onClick={onAdd}
                    disabled={!selectedStrategyGUID}
                    className="flex-1"
                >
                    Add
                </Button>
                <Button type="button" variant="ghost" size="sm" onClick={onCancel} className="flex-1">
                    Cancel
                </Button>
            </div>
        </div>
    );
}
